import { Button, HStack, Input, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useState, type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { StyledLink } from '$/components/core/StyledLink';
import { useToast } from '$/hooks/useToast';
import { type School } from '$/services/usecases/school/mapper/schools';
import { deleteSchool } from '$/services/usecases/school/schools';

interface Props {
  school: School;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeleteSchoolModal: FC<Props> = ({
  school,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useAuthenticationStore.useUser();
  const setUser = useAuthenticationStore.useSetUser();

  const [step, setStep] = useState<1 | 2>(1);
  const [emailInput, setEmailInput] = useState('');

  const isEmailMatch =
    emailInput.trim().toLowerCase() === user?.email.toLowerCase();

  const { mutate: removeSchool, isPending } = useMutation({
    mutationFn: () => deleteSchool(school.id),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(t('general.error'), 'error');
        return;
      }

      if (user) {
        setUser({ ...user, role: 'pro', schoolId: undefined });
      }

      toast(t('school.profile.deleteSchoolModal.deleteSuccess'), 'success');
      onSuccess();
    },
    onError: () => {
      toast(t('general.error'), 'error');
    },
  });

  const handleClose = () => {
    setStep(1);
    setEmailInput('');
    onClose();
  };

  return (
    <FullModal
      isOpen={isOpen}
      onClose={handleClose}
      header={t(`school.profile.deleteSchoolModal.step${step}Title`)}
      size='xl'
      isScrollable={false}
    >
      {step === 1 ? (
        <Stack gap='6'>
          <Stack gap='2'>
            <Text>
              <Trans
                i18nKey='school.profile.deleteSchoolModal.step1Warning'
                values={{ schoolName: school.name }}
                components={{
                  PrivacyLink: (
                    <StyledLink
                      color='primaryButton.background'
                      textDecoration='underline'
                      to='/privacy'
                    />
                  ),
                }}
              />
            </Text>
            <Text mt='4'>
              {t('school.profile.deleteSchoolModal.step1EmailLabel')}
            </Text>
            <Input
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder={user?.email}
              value={emailInput}
            />
          </Stack>
          <HStack justify='space-between'>
            <Button onClick={handleClose} variant='ghost'>
              {t('general.cancel')}
            </Button>
            <Button
              isDisabled={!isEmailMatch}
              onClick={() => setStep(2)}
              variant='danger'
            >
              {t('school.profile.deleteSchoolModal.step1Confirm')}
            </Button>
          </HStack>
        </Stack>
      ) : (
        <Stack gap='6'>
          <Text>
            {t('school.profile.deleteSchoolModal.step2Warning', {
              schoolName: school.name,
            })}
          </Text>
          <HStack justify='space-between'>
            <Button
              isDisabled={isPending}
              onClick={handleClose}
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              isLoading={isPending}
              onClick={() => removeSchool()}
              variant='danger'
            >
              {t('school.profile.deleteSchoolModal.step2Confirm')}
            </Button>
          </HStack>
        </Stack>
      )}
    </FullModal>
  );
};
